import { Injectable, Signal, computed, signal } from '@angular/core';

/** A page that drives the bar while it is the front-most claimant. */
export interface SaveBarOwner {
  /** Whether this page has unsaved changes to show the bar for. */
  visible: Signal<boolean>;
  /** Whether this page's save is in flight (disables the buttons, shows "Saving…"). */
  saving: Signal<boolean>;
  /** Lift the bar above the right-side panel, for a page shown inside it. */
  overPanel?: boolean;
  save: () => void;
  discard: () => void;
}

/**
 * Drives the floating "Save Changes / Remove Changes" bar for the Optimizer and
 * the listing builder. The bar is rendered at the app root (app.component) — as a
 * sibling of the account-manager pill — so it centers on the whole viewport and
 * clears the `contain: layout` on Ionic's `.ion-page`, which otherwise traps a
 * page-level `position: fixed` element inside the offset content column.
 *
 * Both pages can be mounted at once (the builder opens over the Optimizer, as a
 * panel on desktop or a pushed page on mobile), so each claims the bar and the
 * latest claim drives it. Releasing hands it back to the page underneath.
 */
@Injectable({ providedIn: 'root' })
export class OptimizerSaveBarService {
  private readonly owners = signal<SaveBarOwner[]>([]);
  private readonly top = computed(() => this.owners().at(-1) ?? null);

  /** Whether the bar is shown. */
  readonly visible = computed(() => !!this.top()?.visible());
  /** Whether a save is in flight. */
  readonly saving = computed(() => !!this.top()?.saving());
  /** Whether the bar sits above the right-side panel. */
  readonly overPanel = computed(() => !!this.top()?.overPanel);

  /** Make this page the one the bar drives (again, if it already claimed it). */
  claim(owner: SaveBarOwner): void {
    this.owners.update(list => [...list.filter(o => o !== owner), owner]);
  }

  release(owner: SaveBarOwner): void {
    this.owners.update(list => list.filter(o => o !== owner));
  }

  save(): void { this.top()?.save(); }

  discard(): void { this.top()?.discard(); }
}
